<script setup lang="ts">
import { Service } from "@/apps/credit-line/services/network.service";
import Button from "../../../Button.vue";
import { useRouter } from "vue-router";
import { AuthenticationHelper } from "@/apps/credit-line/helpers/authentication.helper";
import { useCreditLineSummary } from "@/apps/credit-line/hooks/useCreditLineSummary";
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import { onBeforeUnmount, ref, watch } from "vue";
import LoadingOverlay from "@/shared-components/ui/core-app/LoadingOverlay.vue";
import { LatestShipmentAddressModel, ShipmentAddressModel } from "../types";
import { ErrorHandler } from "@/apps/credit-line/helpers/errorHandlerWithLogger.helper";
import { AxiosError } from "axios";
import { useHandleReplaceCard } from "../hooks/useHandleReplaceCard";
import Alert from "@/shared-components/common/Alert.vue";

const { summary } = useCreditLineSummary();
const {
  handleStartCardReplacingProcess,
  errorMessage,
  hasError,
  isLoading: isLoadingHandleReplaceCard,
} = useHandleReplaceCard();
const CreditLineService = new Service();
const router = useRouter();
const emit = defineEmits(["back"]);
const { t } = useI18n();
const latestShipment = ref<LatestShipmentAddressModel | null>(null);

const isLoadingLatestAddress = ref<boolean>(true);

const handleBackClick = () => {
  emit("back");
};

const handleContinueClick = async () => {
  if (latestShipment.value)
    await handleStartCardReplacingProcess(latestShipment.value);
};

const handleEditAddress = () => {
  router.replace({ name: "CreditCardShippingAddressView" });
};

const handleGetPhoneNumber = (input: LatestShipmentAddressModel): string => {
  const phoneNumber = summary.value?.customerPhone
    ? summary.value?.customerPhone
    : summary.value?.user.phone
    ? summary.value?.user.phone
    : input?.receiverPhone
    ? input?.receiverPhone
    : "";
  return phoneNumber ? phoneNumber.slice(-10) : "";
};

const fetchAddress = async () => {
  isLoadingLatestAddress.value = true;
  try {
    const response = await CreditLineService.getLatestShipment({
      deviceTag: AuthenticationHelper?.getDeviceTag() || "",
      creditLineId: summary?.value?.creditLineId || 0,
    });
    const userPhone = handleGetPhoneNumber(response.data);

    if (!response?.data?.address || !response?.data?.cardUser || !userPhone) {
      handleEditAddress();
    }
    latestShipment.value = { ...response.data, receiverPhone: userPhone };
  } catch (error) {
    ErrorHandler.handleNetworkErrorAndLog(error as AxiosError);
    handleEditAddress();
  } finally {
    isLoadingLatestAddress.value = false;
  }
};

onBeforeUnmount(() => {
  isLoadingLatestAddress.value = false;
});

watch(
  () => summary.value,
  (newSummary) => {
    if (newSummary) {
      fetchAddress();
    }
  },
  { immediate: true }
);

const getStreetNumber = (address: ShipmentAddressModel | undefined): string => {
  const streetNumber = address?.street_number || "";

  const cleanedStreetNumber = streetNumber
    .replace(/sin\s*n[úu]mero/gi, "")
    .trim();

  return cleanedStreetNumber ? streetNumber : "";
};
</script>

<template>
  <LoadingOverlay
    :loading="isLoadingLatestAddress || isLoadingHandleReplaceCard"
  />
  <div v-if="!isLoadingLatestAddress || !isLoadingHandleReplaceCard">
    <div class="header">
      <i class="fa-solid fa-xmark" @click="handleBackClick"></i>
    </div>
    <div class="body">
      <div>
        <p class="body-title">
          {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_3.TITLE") }}
        </p>
      </div>
      <div class="container-address">
        <div class="address-header">
          <p class="address-header-title">
            {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_3.DESCRIPTION") }}
          </p>
          <p class="address-header-edit" @click="handleEditAddress">
            {{ t("REPORT_LOST_OR_STOLEN_CARD.VIEW_3.EDIT_ADDRESS") }}
          </p>
        </div>
        <div class="address-body">
          <p class="address-body-text">
            {{ latestShipment?.address?.city }} <br />
            {{ latestShipment?.address?.region }} <br />
            {{ latestShipment?.address?.street_name }}
            {{ getStreetNumber(latestShipment?.address) }} <br />
            {{ latestShipment?.address?.additional_info }}
            <br v-if="!!latestShipment?.address?.additional_info" />
            {{
              !!latestShipment?.address?.zip_code &&
              "CP: " + latestShipment?.address.zip_code
            }}
          </p>
        </div>
      </div>
      <div class="container-error" v-if="hasError">
        <Alert
          :message="t(errorMessage)"
          alertType="alert-danger"
          :closable="true"
          alertIcon="fa fa-exclamation-triangle"
          @onClose="hasError = false"
        />
      </div>
    </div>
    <div class="footer">
      <Button @click="handleContinueClick">{{
        t("REPORT_LOST_OR_STOLEN_CARD.VIEW_3.CTA")
      }}</Button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.fa-xmark {
  font-size: 24px;
}
.header {
  display: flex;
  justify-content: flex-end;
  padding: 20px 16px;
}
.body {
  padding: 30px 20px 30px 20px;
  .body-title {
    font-weight: 600;
    font-size: 20px;
    line-height: 29px;
    color: #000000;
    text-align: center;
  }
}
.container-address {
  margin-top: 32px;
  border-radius: 10px;
  border: 1px solid #dddddd;
  background: #f2f2f2;
}
.address-header {
  display: flex;
  justify-content: space-between;
  padding: 16px 14px;
  border-bottom: 1px solid #dddddd;
  .address-header-title {
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    color: #111111;
  }
  .address-header-edit {
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    color: #2c7040;
  }
}
.address-body-text {
  padding: 16px;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  color: #111111;
}
.footer {
  margin: 100px 0px 40px 0px;
  padding: 0 20px;
}
.container-error {
  margin-top: 20px;
}
</style>
